// Align an arbitrary portrait into a fixed frame by its eye landmarks, as a
// CSS transform.
//
// WHY THIS EXISTS
// The glowup wipe puts two images of the same face on top of each other and
// slides a divider between them. That reads as "the same face, changed" only
// if the two faces actually register. They do not, by construction:
//
//   scan  ─ applyPhotoTreatment ─▶ processed_image_path   3:4  (1080×1440-ish)
//         └─ gpt-image-2 edit ───▶ render                 2:3  (1024×1536)
//
// The image model is asked for a fixed portrait size that is NOT the scan's
// aspect, so it recomposes the frame — the face lands at a different scale and
// a different height. `object-cover` then crops each one differently and the
// wipe shows two people sliding past each other.
//
// Cropping cannot fix this, because the model is free to move the face within
// its own output. The only thing both images genuinely share is the FACE, so
// the face is what we align on: detect eye centres in each, then place both
// eye-midpoints on the same point of the frame at the same interocular
// distance. Whatever either image did with its framing stops mattering.
//
// Same similarity transform as facePrep's canvas normalize (translate → scale
// → deroll → translate), expressed as a CSS matrix instead of a drawImage.
// Constants are shared with it, so a portrait aligned here sits where the scan
// pipeline would have put it.
//
// FAILS SOFT, ALWAYS. No landmarker, no face, a cross-origin image the browser
// will not let us read — every one of those returns null and the caller falls
// back to plain object-cover. An unaligned wipe is the status quo; a broken
// modal is not.
import {
  EYE_MID_X_FRAC,
  EYE_MID_Y_FRAC,
  TARGET_IOD_FRAC,
  MAX_UPSCALE,
  COVER_SCALE_MAX,
} from './constants';
import { eyeCentersPx } from './geometry';
import { getLandmarkerWithTimeout } from './landmarker';

export const IMAGE_LOAD_TIMEOUT_MS = 6000;

/**
 * Load an image for pixel access.
 *
 * `crossOrigin = 'anonymous'` is REQUIRED and is also the most likely reason
 * this whole path no-ops in production: the landmarker reads pixels, which
 * taints on a cross-origin image, and signed Storage URLs only send
 * Access-Control-Allow-Origin if the BUCKET has a CORS rule. Without one the
 * load rejects here and alignment is skipped — deliberately, and silently.
 */
function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // A stalled request fires NEITHER onload nor onerror, so without this the
    // promise never settles and the caller waits forever. Alignment is a
    // nice-to-have on a modal that has already rendered; it gets a deadline.
    const timer = setTimeout(() => reject(new Error('image load timed out')), IMAGE_LOAD_TIMEOUT_MS);
    const done = (fn) => (arg) => { clearTimeout(timer); fn(arg); };
    img.crossOrigin = 'anonymous';
    img.onload = done(() => resolve(img));
    img.onerror = done(() => reject(new Error('image load failed')));
    img.src = url;
  });
}

/** Smallest scale at which the frame is fully covered by source pixels. */
function minCoverScale(theta, mid, target, srcW, srcH, frameW, frameH) {
  const cos = Math.cos(theta);
  const sin = Math.sin(theta);
  let sMin = 0;
  [[0, 0], [frameW, 0], [0, frameH], [frameW, frameH]].forEach(([cx, cy]) => {
    const dx = cx - target.x;
    const dy = cy - target.y;
    const vx = dx * cos - dy * sin;
    const vy = dx * sin + dy * cos;
    const need = (v, room) => (room > 0 ? v / room : Infinity);
    if (vx > 0) sMin = Math.max(sMin, need(vx, srcW - mid.x));
    if (vx < 0) sMin = Math.max(sMin, need(-vx, mid.x));
    if (vy > 0) sMin = Math.max(sMin, need(vy, srcH - mid.y));
    if (vy < 0) sMin = Math.max(sMin, need(-vy, mid.y));
  });
  return sMin;
}

/**
 * @param {{natW:number,natH:number,left:object,right:object}} face
 * @param {number} frameW @param {number} frameH
 * @returns {{width:number,height:number,transform:string}|null} styles for an
 *   <img> positioned at natural size with transform-origin 0 0.
 */
export function frameTransform(face, frameW, frameH) {
  if (!face || !frameW || !frameH) return null;
  const { natW, natH, left, right } = face;
  const dx = right.x - left.x;
  const dy = right.y - left.y;
  const iod = Math.hypot(dx, dy);
  if (!iod) return null;

  const theta = Math.atan2(dy, dx);
  const mid = { x: (left.x + right.x) / 2, y: (left.y + right.y) / 2 };
  const target = { x: EYE_MID_X_FRAC * frameW, y: EYE_MID_Y_FRAC * frameH };
  const sTarget = Math.min((TARGET_IOD_FRAC * frameW) / iod, MAX_UPSCALE);
  const sCover = Math.min(
    minCoverScale(theta, mid, target, natW, natH, frameW, frameH),
    COVER_SCALE_MAX,
  );
  const s = Math.max(sTarget, sCover);

  // CSS applies transforms RIGHT TO LEFT, so this reads bottom-up as:
  // move the eye midpoint to the origin, scale, deroll, move to the target.
  return {
    width: natW,
    height: natH,
    transform: [
      `translate(${target.x}px, ${target.y}px)`,
      `rotate(${-theta}rad)`,
      `scale(${s})`,
      `translate(${-mid.x}px, ${-mid.y}px)`,
    ].join(' '),
  };
}

/**
 * Detect a face in `url`.
 * @returns {Promise<{natW,natH,left,right}|null>} null on ANY failure.
 */
export async function detectFace(url) {
  if (!url) return null;
  try {
    const loaded = await getLandmarkerWithTimeout();
    if (!loaded) return null;
    const img = await loadImage(url);
    const natW = img.naturalWidth;
    const natH = img.naturalHeight;
    if (!natW || !natH) return null;
    const result = loaded.landmarker.detect(img);
    const marks = result && result.faceLandmarks && result.faceLandmarks[0];
    if (!marks || !marks.length) return null;
    const { left, right } = eyeCentersPx(marks, natW, natH);
    return { natW, natH, left, right };
  } catch (err) {
    // Includes the cross-origin case above. Never throws to the caller.
    return null;
  }
}

/**
 * Detect BOTH portraits. All-or-nothing on purpose: aligning one image while
 * the other keeps object-cover would move the faces further apart, not closer.
 * @returns {Promise<{before,after}|null>}
 */
export async function detectPair(beforeUrl, afterUrl) {
  const [before, after] = await Promise.all([
    detectFace(beforeUrl),
    detectFace(afterUrl),
  ]);
  return before && after ? { before, after } : null;
}
